import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Layout from '@/components/layout/Layout'
import RoutePrefetcher from '@/components/common/RoutePrefetcher'
import RouteTransition from '@/components/common/RouteTransition'
import { InteractionProvider } from '@/context/InteractionContext'
import DelayedPageLoader from '@/components/common/DelayedPageLoader'
import Breadcrumb from '@/components/layout/Breadcrumb'
import ErrorBoundary from '@/components/common/ErrorBoundary'

const HomePage = lazy(() => import('@/pages/HomePage'))
const AboutPage = lazy(() => import('@/pages/AboutPage'))
const ShowcasePage = lazy(() => import('@/pages/DemosPage'))
const GalleryPage = lazy(() => import('@/pages/GalleryPage'))
const DemoDetailPage = lazy(() => import('@/pages/DemoDetailPage'))
const CollectionDetailPage = lazy(() => import('@/pages/CollectionDetailPage'))
const DayPage = lazy(() => import('@/pages/DayPage'))
const ShowcaseRotatingCubePage = lazy(() => import('@/pages/demos/RotatingCubePage'))
const BlogListPage = lazy(() => import('@/pages/BlogListPage'))
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage'))
const DocsPage = lazy(() => import('@/pages/DocsPage'))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'))
const TermsPage = lazy(() => import('@/pages/TermsPage'))
const RumPage = lazy(() => import('@/pages/RumPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))
// New concept placeholder pages
const PlaygroundPage = lazy(() => import('@/pages/PlaygroundPage'))
const AIAssistantPage = lazy(() => import('@/pages/AIAssistantPage'))
const CommunityPage = lazy(() => import('@/pages/CommunityPage'))
const LearnPage = lazy(() => import('@/pages/LearnPage'))
const SitemapPage = lazy(() => import('@/pages/SitemapPage'))
const ObservatoryPage = lazy(() => import('@/pages/ObservatoryPage'))
const HubblePage = lazy(() => import('@/pages/HubblePage'))
const JWSTPage = lazy(() => import('@/pages/JWSTPage'))

function App() {
  const location = useLocation()
  // Note: This validates demo configuration without using the result

  return (
    <InteractionProvider>
      <Layout>
        <RouteTransition>
          <ErrorBoundary>
          <Suspense fallback={<DelayedPageLoader /> }>
            <Routes location={location}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            {/* /contact・/demos 一覧は非公開（壊れているためホームへ退避。個別デモ /demos/:slug は生きている） */}
            <Route path="/contact" element={<Navigate to="/" replace />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/showcase" element={<ShowcasePage />} />
            <Route path="/showcase/rotating-cube" element={<ShowcaseRotatingCubePage />} />
            <Route path="/showcase/:id" element={<CollectionDetailPage />} />
            <Route path="/day/:id" element={<DayPage />} />
            <Route path="/demos" element={<Navigate to="/" replace />} />
            <Route path="/demos/:slug" element={<DemoDetailPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/docs" element={<DocsPage />} />
            {/* New concept routes */}
            <Route path="/playground" element={<PlaygroundPage />} />
            <Route path="/ai-assistant" element={<AIAssistantPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/learn" element={<LearnPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/rum" element={<RumPage />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route path="/observatory" element={<ObservatoryPage />} />
            <Route path="/observatory/hubble" element={<HubblePage />} />
            <Route path="/observatory/jwst" element={<JWSTPage />} />
            <Route path="/hubble" element={<Navigate to="/observatory/hubble" replace />} />
            <Route path="/jwst" element={<Navigate to="/observatory/jwst" replace />} />
            <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </RouteTransition>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb />
        </div>
        <RoutePrefetcher />
      </Layout>
    </InteractionProvider>
  )
}

export default App
